import React, { useEffect, useMemo, useState } from 'react';
import { Delete as DeleteIcon, Plus, Drag as DragIcon } from '@icon-park/react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './styles/ProductBasicInfo.css';

// 将 SortableField 移到组件外部，避免每次渲染时重新创建组件导致输入框失焦
function SortableField({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1,
    boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.12)' : undefined
  };
  return (
    <div ref={setNodeRef} style={style} className="form-row custom-field sortable-field">
      {children({ attributes, listeners })}
    </div>
  );
}

function ProductBasicInfo({ data, onUpdate }) {
  const [expanded, setExpanded] = useState(true);
  const fields = useMemo(() => ([
    { key: 'productName', label: '产品名称', placeholder: 'xxx' },
    { key: 'ingredients', label: '配料表', placeholder: 'xxx' },
    { key: 'netContent', label: '净含量/规格', placeholder: 'xxx' },
    { key: 'standardCode', label: '产品标准代号', placeholder: 'xxx' },
    { key: 'manufacturer', label: '生产商', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'address', label: '地址', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'origin', label: '产地', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'productionDate', label: '生产日期', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'shelfLife', label: '保质期', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'storageConditions', label: '贮存条件', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'productionLicense', label: '食品生产许可证编号', placeholder: '请输入内容，提示：主播写的时日子进行描示' },
    { key: 'serviceHotline', label: '消费者服务热线', placeholder: '请输入内容，提示：主播写的时日子进行描示' }
  ]), []);

  const handleFieldChange = (key, value) => {
    const next = localItems.map(item => item._id === key ? { ...item, value } : item);
    setLocalItems(next);
    scheduleSync(next);
  };

  const getCustomIndexById = (id) => {
    return (data.customFields || []).findIndex(f => f._id === id);
  };

  const handleAddField = () => {
    const newFields = data.customFields || [];
    const newItem = { label: '新字段', value: '', _id: `cf-${Date.now()}-${newFields.length}` };
    const updatedFields = [...newFields, newItem];
    const baseIds = fields.map(f => f.key);
    const existingOrder = data.fieldOrder || [...baseIds, ...newFields.map(f => f._id || '')];
    const updatedOrder = existingOrder.concat([newItem._id]);
    onUpdate({ ...data, customFields: updatedFields, fieldOrder: updatedOrder });
  };

  const handleCustomFieldChange = (id, field, value) => {
    const next = localItems.map(item => {
      if (item._id !== id) return item;
      if (field === 'label') return { ...item, label: value };
      if (field === 'value') return { ...item, value };
      return item;
    });
    setLocalItems(next);
    scheduleSync(next);
  };

  const handleDeleteCustomField = (id) => {
    const filtered = localItems.filter(item => item._id !== id);
    setLocalItems(filtered);
    scheduleSync(filtered);
  };

  // 初始化自定义字段 id & 字段排序
  useEffect(() => {
    const custom = data.customFields || [];
    const timestamp = Date.now();
    let changed = false;
    const updatedCustom = custom.map((f, idx) => {
      if (!f._id) {
        changed = true;
        return { ...f, _id: `cf-${timestamp}-${idx}` };
      }
      return f;
    });

    const baseIds = fields.map(f => f.key);
    const customIds = updatedCustom.map(f => f._id);
    const defaultOrder = [...baseIds, ...customIds];
    const existingOrder = data.fieldOrder || [];

    const mergedOrder = [
      ...existingOrder.filter(id => defaultOrder.includes(id)),
      ...defaultOrder.filter(id => !existingOrder.includes(id))
    ];

    const orderChanged = mergedOrder.length !== existingOrder.length ||
      mergedOrder.some((id, idx) => id !== existingOrder[idx]);

    if (changed || orderChanged) {
      onUpdate({ ...data, customFields: updatedCustom, fieldOrder: mergedOrder });
    }
    // 仅在自定义字段或fieldOrder缺失时运行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.customFields]);

  const allItems = useMemo(() => {
    const baseItems = fields.map(f => ({ ...f, _id: f.key, type: 'base', value: data[f.key] || '' }));
    const customItems = (data.customFields || []).map(f => ({ ...f, _id: f._id, type: 'custom' }));
    const order = data.fieldOrder || [...baseItems.map(i => i._id), ...customItems.map(i => i._id)];
    const map = [...baseItems, ...customItems].reduce((acc, item) => {
      acc[item._id] = item;
      return acc;
    }, {});
    return order.map(id => map[id]).filter(Boolean);
  }, [data, fields]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const [localItems, setLocalItems] = useState(allItems);
  const syncTimer = React.useRef(null);

  // 同步 props -> local（只在项数量或 id 变化时）
  useEffect(() => {
    const sameLength = localItems.length === allItems.length;
    const sameIds = sameLength && localItems.every((item, idx) => item?._id === allItems[idx]?._id);
    if (!sameIds) {
      setLocalItems(allItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems.length, allItems.map(i => i._id).join(',')]);

  // 将本地状态同步回数据（节流，防止输入时频繁重渲染导致失焦）
  const scheduleSync = (nextItems) => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      const updatedData = { ...data };
      // 基础字段写回
      nextItems.forEach(item => {
        if (item.type === 'base') {
          updatedData[item.key] = item.value || '';
        }
      });
      // 自定义字段写回
      const customList = nextItems.filter(i => i.type === 'custom').map(({ type, ...rest }) => rest);
      updatedData.customFields = customList;
      // 顺序写回
      updatedData.fieldOrder = nextItems.map(i => i._id);
      onUpdate(updatedData);
    }, 120);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localItems.findIndex(f => f._id === active.id);
    const newIndex = localItems.findIndex(f => f._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(localItems, oldIndex, newIndex);
    setLocalItems(reordered);
    scheduleSync(reordered);
  };

  return (
    <div className="product-basic-info-container">
      <div className="template-header">
        <div className="template-header-main">
          <h3 className="template-title">输入产品基本信息</h3>
          <button className="template-toggle" onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? '折叠' : '展开'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="info-form">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={allItems.map(f => f._id)}>
            {localItems.map((field) => (
              <SortableField key={field._id} id={field._id}>
                {({ attributes, listeners }) => (
                  <>
                    {field.type === 'base' ? (
                      <label className="form-label">{field.label}</label>
                    ) : (
                      <input
                        type="text"
                        className="form-label-input"
                        placeholder="字段名称"
                        value={field.label}
                        onChange={(e) => handleCustomFieldChange(field._id, 'label', e.target.value)}
                      />
                    )}
                    <textarea
                      className="form-textarea"
                      placeholder={field.placeholder || '字段内容'}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        if (field.type === 'base') handleFieldChange(field._id, e.target.value);
                        else handleCustomFieldChange(field._id, 'value', e.target.value);
                      }}
                      rows={field.key === 'ingredients' ? 3 : 2}
                    />
                    <div className="custom-field-actions">
                      <button
                        className="drag-handle"
                        {...attributes}
                        {...listeners}
                        title="拖拽排序"
                        type="button"
                      >
                        <DragIcon theme="outline" size={16} fill="#111827" strokeWidth={3} />
                      </button>
                      {field.type === 'custom' && (
                        <button
                          className="delete-field-btn"
                          onClick={() => handleDeleteCustomField(field._id)}
                          type="button"
                        >
                          <DeleteIcon theme="outline" size={14} fill="#333" strokeWidth={3} />
                        </button>
                      )}
                      {field.type === 'base' && (
                        <button
                          className="delete-field-btn disabled-delete"
                          title="默认字段不可删除"
                          type="button"
                          disabled
                        >
                          <DeleteIcon theme="outline" size={14} fill="#999" strokeWidth={3} />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </SortableField>
            ))}
          </SortableContext>
        </DndContext>

        <button className="add-field-btn" onClick={handleAddField}>
          <span className="add-icon"><Plus theme="outline" size={18} fill="#333" strokeWidth={3} /></span>
          添加自定义字段
        </button>

        {/* 表格样式设置 */}
        <div className="table-style-config-card">
          <div className="config-card-header">
            <span className="config-card-title">表格样式</span>
          </div>
          <div className="config-card-body">
            <div className="table-style-grid">
              <div className="style-item">
                <label className="style-label">标签列背景</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    className="style-color-picker"
                    value={(data.tableStyle && data.tableStyle.labelBgColor) || '#ffffff'} 
                    onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), labelBgColor: e.target.value } })} 
                  />
                  <span className="color-value-text">{(data.tableStyle && data.tableStyle.labelBgColor) || '#ffffff'}</span>
                </div>
              </div>
              <div className="style-item">
                <label className="style-label">标签列文字</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    className="style-color-picker"
                    value={(data.tableStyle && data.tableStyle.labelTextColor) || '#5C4A37'} 
                    onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), labelTextColor: e.target.value } })} 
                  />
                  <span className="color-value-text">{(data.tableStyle && data.tableStyle.labelTextColor) || '#5C4A37'}</span>
                </div>
              </div>
              <div className="style-item">
                <label className="style-label">值列文字</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    className="style-color-picker"
                    value={(data.tableStyle && data.tableStyle.valueTextColor) || '#202124'} 
                    onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), valueTextColor: e.target.value } })} 
                  />
                  <span className="color-value-text">{(data.tableStyle && data.tableStyle.valueTextColor) || '#202124'}</span>
                </div>
              </div>
              <div className="style-item">
                <label className="style-label">行分隔线</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    className="style-color-picker"
                    value={(data.tableStyle && data.tableStyle.rowDividerColor) || '#D4BFA0'} 
                    onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), rowDividerColor: e.target.value } })} 
                  />
                  <span className="color-value-text">{(data.tableStyle && data.tableStyle.rowDividerColor) || '#D4BFA0'}</span>
                </div>
              </div>
              <div className="style-item">
                <label className="style-label">单元格内边距</label>
                <div className="style-input-group">
                  <input 
                    type="number" 
                    className="style-input-number"
                    min="4" 
                    max="24" 
                    value={(data.tableStyle && data.tableStyle.cellPadding) || 12} 
                    onChange={(e) => onUpdate({ ...data, tableStyle: { ...(data.tableStyle || {}), cellPadding: Number(e.target.value) } })} 
                  />
                  <span className="style-unit">px</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}

export default ProductBasicInfo;
